import { useEffect, useState, ReactNode } from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import sayings from '@/data/teacherSayings.json';

function useRotatingSaying(intervalMs = 8000) {
  const [index, setIndex] = useState(() => Math.floor(Math.random() * sayings.length));
  useEffect(() => {
    const timer = setInterval(() => setIndex((i) => (i + 1) % sayings.length), intervalMs);
    return () => clearInterval(timer);
  }, [intervalMs]);
  return sayings[index];
}

export function AuthLayout({ children }: { children: ReactNode }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const saying = useRotatingSaying();

  return (
    <Box sx={{
      position: 'fixed', inset: 0,
      display: 'flex',
      overflow: 'hidden',
    }}>
      {/* Left panel — 40% on desktop, 100% on mobile */}
      <Box
        sx={{
          width: { xs: '100%', md: '40%' },
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          px: { xs: 3, sm: 6 },
          py: { xs: 3, sm: 5 },
          overflowY: 'auto',
          bgcolor: isDark ? 'background.default' : 'background.paper',
        }}
      >
        {/* Logo */}
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 5 }}>
            <Box sx={{
              width: 32, height: 32, borderRadius: '8px',
              bgcolor: 'primary.main', display: 'flex',
              alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}>
              <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: 16, lineHeight: 1 }}>T</Typography>
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 700, letterSpacing: '-0.5px' }}>
              Timetable
            </Typography>
          </Box>

          {/* Page-specific form content */}
          {children}
        </Box>

        <Typography variant="caption" color="text.disabled">
          Powered by{' '}
          <Box component="span" sx={{ fontWeight: 700, color: 'text.secondary' }}>
            Santanix Digital Lab
          </Box>
        </Typography>
      </Box>

      {/* Right panel — 60% */}
      <Box sx={{
        width: '60%',
        flexShrink: 0,
        display: { xs: 'none', md: 'flex' },
        flexDirection: 'column',
        justifyContent: 'center', alignItems: 'flex-start',
        px: 8, py: 8,
        bgcolor: isDark ? 'primary.dark' : 'primary.main',
        position: 'relative', overflow: 'hidden',
      }}>
        <Box sx={{ position: 'absolute', top: -80, right: -80, width: 320, height: 320, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.06)' }} />
        <Box sx={{ position: 'absolute', bottom: -60, left: -60, width: 240, height: 240, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.06)' }} />

        <Typography sx={{ fontSize: 120, lineHeight: 0.8, color: 'rgba(255,255,255,0.2)', fontFamily: 'Georgia, serif', mb: 2, userSelect: 'none' }}>
          "
        </Typography>
        <Typography variant="h5" sx={{ color: '#fff', fontWeight: 400, lineHeight: 1.6, maxWidth: 520, mb: 4, fontStyle: 'italic' }}>
          {saying.quote}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{ width: 36, height: 36, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: 14 }}>{saying.author.charAt(0)}</Typography>
          </Box>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)', fontWeight: 500 }}>
            {saying.author}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}
